import React from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { ModalDialog } from "./ModalDialog";
import { useSettingsStore } from "../store/settingsStore";
import { normalizationTypes } from "../utils/normalization";
import { analyzeWorkflows, fixingWorkflows, shortenWorkflows } from "../utils/workflows";
import { Switch } from "@headlessui/react";

interface SettingsDialogProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({ isOpen, setOpen }) => {
  const {
    theme,
    setTheme,
    displayHiddenFragments,
    toggleDisplayHiddenFragments,
    normalizationType,
    setSetting,
    analyzeWorkflowName,
    shortenWorkflowName,
    fixingWorkflowName,
    apiUrl,
    setApiUrl
  } = useSettingsStore();

  return (
    <ModalDialog open={isOpen} setOpen={setOpen} title="App Settings">
      <form className="space-y-4">
        {/* Theme */}
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Theme</label>
          <div className="w-1/2">
            <select
              value={theme}
              onChange={(e) => setTheme(e.target.value as string)}
              className="mt-1 block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>

        {/* Toggle Hidden Fragments */}
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Show Hidden Fragments</label>
          <div className="w-1/2">
            <Switch
              checked={displayHiddenFragments}
              onChange={toggleDisplayHiddenFragments}
              className={`${displayHiddenFragments ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-700'}
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${displayHiddenFragments ? 'translate-x-6' : 'translate-x-1'
                  }`}
              />
            </Switch>
          </div>
        </div>

        {/* Normalization */}
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Normalization</label>
          <div className="w-1/2">
            <select
              value={normalizationType}
              onChange={(e) => setSetting("normalizationType", e.target.value as typeof normalizationType)}
              className="mt-1 block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {normalizationTypes.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Workflows */}
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Analyze Workflow</label>
          <div className="w-1/2">
            <select
              value={analyzeWorkflowName}
              onChange={(e) => setSetting("analyzeWorkflowName", e.target.value as typeof analyzeWorkflowName)}
              className="mt-1 block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {analyzeWorkflows.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Shorten Workflow</label>
          <div className="w-1/2">
            <select
              value={shortenWorkflowName}
              onChange={(e) => setSetting("shortenWorkflowName", e.target.value as typeof shortenWorkflowName)}
              className="mt-1 block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {shortenWorkflows.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Fixing Workflow</label>
          <div className="w-1/2">
            <select
              value={fixingWorkflowName}
              onChange={(e) => setSetting("fixingWorkflowName", e.target.value as typeof fixingWorkflowName)}
              className="mt-1 block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {fixingWorkflows.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* API URL */}
        {(() => {
          // Custom API URL selector logic
          const [tempUrl, setTempUrl] = React.useState(apiUrl || "");
          const [mode, setMode] = React.useState(
            ["https://api.seenai.co", "https://dev-api.seenai.co"].includes(apiUrl ?? "") ? apiUrl : "custom"
          );
          const showActions = tempUrl !== apiUrl;

          // Keep tempUrl and mode in sync with apiUrl when dialog is opened
          React.useEffect(() => {
            setTempUrl(apiUrl || "");
            setMode(
              ["https://api.seenai.co", "https://dev-api.seenai.co"].includes(apiUrl ?? "") ? apiUrl : "custom"
            );
          }, [apiUrl, isOpen]);

          return (
            <div className="flex items-center justify-between gap-4">
              <label className="text-sm text-gray-700 dark:text-gray-300 w-1/2">Base URL</label>
              <div className="w-1/2 space-y-2">
                <select
                  value={mode}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setMode(selected);
                    if (selected === "custom") {
                      setTempUrl(apiUrl ?? "");
                    } else {
                      setTempUrl(selected);
                    }
                  }}
                  className="block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="https://api.seenai.co">https://api.seenai.co</option>
                  <option value="https://dev-api.seenai.co">https://dev-api.seenai.co</option>
                  <option value="custom">Custom</option>
                </select>

                {mode === "custom" && (
                  <input
                    type="text"
                    value={tempUrl}
                    onChange={(e) => setTempUrl(e.target.value)}
                    placeholder="https://your.custom.api"
                    className="block w-full rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                )}
              </div>

              {showActions && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      // Reset tempUrl and mode to current saved apiUrl
                      setTempUrl(apiUrl || "");
                      setMode(
                        ["https://api.seenai.co", "https://dev-api.seenai.co"].includes(apiUrl ?? "") ? apiUrl : "custom"
                      );
                    }}
                    className="text-red-500 hover:text-red-700"
                    title="Cancel"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setApiUrl(tempUrl);
                    }}
                    className="text-green-600 hover:text-green-800"
                    title="Save"
                  >
                    <CheckCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </form>
    </ModalDialog>
  );
};